import {TestResultFormType} from "../../../types/user";
import {useCallback, useState} from "react";
import {
    getDcasProfileTestHistoryAsync,
    getProfileAuthTokenAsync,
    getProfileTestHistoryAsync
} from "../../../store/profile/profileSlice";
import {useAppDispatch} from "../../../hooks/hooks";
import {BrainType} from "../../../types/review";
import {useTranslation} from "react-i18next";

export function useCheckupInfoFunctions() {
    const {t} = useTranslation("checkupInfo");
    const dispatch = useAppDispatch();
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [profileData, setProfileData] = useState<any[]>([]);
    const [pageIdx, setPageIdx] = useState(0);

    // 토큰 취득
    const getAuthToken = useCallback(
        async (param: { userProfileIdx: number }) => {
            try {
                const response: any = await dispatch(getProfileAuthTokenAsync(param)).unwrap();

                if (response && typeof response === "object" && "accessToken" in response) {
                    setAccessToken(response.accessToken);
                } else {
                    console.warn("응답에 Access Token이 없습니다.");
                }
            } catch (error) {
                console.error("인증 토큰을 가져오는 중 오류 발생:", error);
            }
        },
        [dispatch]
    );

    // 검사결과지
    const handleOpenTestResult = (
        executedInfoIdx: number,
        unitVoucherType: "KPASS" | "DCAS",
        kpassResultForm: TestResultFormType,
        dcasResltForm: TestResultFormType
    ) => {
        if (!accessToken) {
            console.warn("Access Token이 없습니다. 새 창을 열 수 없습니다.");
            return;
        }

        const baseUrl = `${process.env.REACT_APP_BASE_URL}api/v1/`;
        const type = unitVoucherType.toLowerCase();
        const form = unitVoucherType === "KPASS" ? kpassResultForm : dcasResltForm;

        const path = form === TestResultFormType.SUMMARY
            ? `/organ-manager/test-result/summary/${executedInfoIdx}/${accessToken}`
            : `/profile/test-result/${executedInfoIdx}/${accessToken}`;

        const returnUrl = baseUrl + type + path;

        if (unitVoucherType === "KPASS") {
            console.log('쿠키 추가')
            document.cookie = "fromHomepage=true; domain=.feel-good.io; path=/; max-age=3600; Secure; SameSite=Lax";
        }
        window.open(returnUrl, "_blank");
    };

    // 학습진로가이드
    const handleOpenGuide = (executedInfoIdx: number, unitVoucherType: "KPASS" | "DCAS") => {
        if (!accessToken) {
            console.warn("Access Token이 없습니다. 가이드 페이지를 열 수 없습니다.");
            return;
        }

        const baseUrl = `${process.env.REACT_APP_BASE_URL}api/v1/`;
        const type = unitVoucherType.toLowerCase();
        const url = baseUrl + type + `/profile/test-result/${executedInfoIdx}/recommend-job?token=${accessToken}`;

        if (unitVoucherType === "KPASS") {
            console.log('쿠키 추가')
            document.cookie = "fromHomepage=true; domain=.feel-good.io; path=/; max-age=3600; Secure; SameSite=Lax";
        }
        window.open(url, "_blank");
    };

    const getProfileInfo = useCallback(
        async ({profileIdx}: { profileIdx: number }) => {
            try {
                // 새로운 데이터를 가져오기 전에 profileData 상태를 초기화합니다.
                setProfileData([]); // profileIdx가 변경될 때마다 데이터를 초기화합니다.

                const response = await dispatch(getProfileTestHistoryAsync({profileIdx})).unwrap();
                const response2 = await dispatch(getDcasProfileTestHistoryAsync({profileIdx})).unwrap();

                const combinedData = [...(response?.content ?? []), ...(response2?.content ?? [])].sort(
                    (a, b) => new Date(b.endedAt).getTime() - new Date(a.endedAt).getTime()
                );

                setProfileData(combinedData);
                setPageIdx(0);
            } catch (error) {
                console.log(error);
            }
        },
        [dispatch]
    );


    // 약식결과지인 경우 FALSE, 아닌 경우 TRUE를 반환하는 함수
    const handleGuideVisible = (
        unitVoucherType: "KPASS" | "DCAS",
        kpassResultType: TestResultFormType,
        dcasResultType: TestResultFormType
    ) => {
        if (unitVoucherType === "KPASS") {
            return kpassResultType !== TestResultFormType.SUMMARY;
        } else {
            return dcasResultType !== TestResultFormType.SUMMARY;
        }
    };

    const handleBrainType = (brainType: BrainType) => {
        switch (brainType) {
            case BrainType.BALANCED:
                return t('detail.row.balanceBrain');
            case BrainType.SIMULTANEOUS:
                return t('detail.row.rightBrain')
            case BrainType.SUCCESSIVE:
                return t('detail.row.leftBrain')
            default:
                return t('detail.row.rightBrain')
        }
    }

    return {
        getAuthToken,
        handleOpenTestResult, handleOpenGuide,
        handleGuideVisible, getProfileInfo, profileData,
        accessToken, pageIdx, setPageIdx,
        handleBrainType
    };
}